import { cva } from "class-variance-authority";
import React from "react";
import { PropsWithChildren, ReactNode } from "react";

import { InputFieldProvider, type InputInfoContent, useInputField } from "./InputContext";
import { InputError } from "./components/InputError";
import { InputLabel } from "./components/InputLabel";

import { cn } from "../../../lib/utils";

import { AnimatePresence, SubtleTopSlideIn } from "../../organisms/AnimatePresence";

export { TitleLabel } from "./components/TitleLabel";
export type { InputInfoContent } from "./InputContext";

export type InputWrapperProps = {
	/**
	 * The id of the input field, will get applied to all inner inputs unless overridden with another input wrapper OR one is directly set on the input
	 */
	id: string;
	/**
	 * sets error state on all inner inputs that use the useInputField context hook and renders an error message under the input
	 */
	error?: string;
	/**
	 * disables all inner inputs that use the useInputField context hook
	 */
	disabled?: boolean;
	/**
	 * makes all inner inputs that use the useInputField context hook read-only
	 */
	readOnly?: boolean;
	/**
	 * renders an asterisk after the label, and sets aria-required on inner inputs whose
	 * role supports it (text inputs, radio groups, checkboxes, switches, the text editor). Controls
	 * whose trigger is a button role -- the selects and the date range picker -- get the
	 * asterisk only, since aria-required is not mapped on that role.
	 * Display only, it runs no validation. Not inherited by nested input wrappers.
	 * @default false
	 */
	required?: boolean;
	/**
	 * The label of the input field
	 */
	label?: string | ReactNode;
	/**
	 * The position of the label, either top, left or right
	 * @default top
	 */
	labelPosition?: "top" | "left" | "right";
	/**
	 * The justify-content value for the flex container when labelPosition is left or right
	 * @default "start"
	 */
	justify?: "start" | "end" | "center" | "between" | "around" | "evenly";
	/**
	 * The align-items value for the flex container when labelPosition is left or right
	 * @default "center"
	 */
	align?: "start" | "end" | "center" | "baseline" | "stretch";
	/**
	 * The info message of the input field, renders as a tooltip on the right of the label.
	 * Can be a plain string or an object with title, message, and optional titleBottomLine.
	 */
	info?: InputInfoContent;
	/**
	 * Whether the wrapper is the group legend
	 * @default false
	 */
	group?: boolean;
	/**
	 * Whether to disable label click behavior (htmlFor attribute)
	 * @default false
	 */
	disableLabelClick?: boolean;
	/**
	 * Content displayed right-aligned next to the label.
	 * Accepts any React node (e.g., counters, badges, custom indicators).
	 */
	labelRight?: ReactNode;

	/**
	 * The size of the label text
	 */
	labelSize?: "paragraph-1" | "paragraph-2" | "paragraph-3" | "caption";
	/**
	 * The weight of the label text
	 */
	labelWeight?: "regular" | "semi-bold" | "bold";

	dangerouslySetClassName?: string;

	"data-testid"?: string;
};

const inputWrapperVariants = cva("flex gap-2", {
	variants: {
		labelPosition: {
			top: "flex-col",
			left: "flex-row",
			right: "flex-row",
		},
		justify: {
			start: "justify-start",
			end: "justify-end",
			center: "justify-center",
			between: "justify-between",
			around: "justify-around",
			evenly: "justify-evenly",
		},
		align: {
			start: "items-start",
			end: "items-end",
			center: "items-center",
			baseline: "items-baseline",
			stretch: "items-stretch",
		},
	},
	defaultVariants: {
		labelPosition: "top",
	},
});

/**
 * InputWrapper is a component that wraps an input field and provides a label, error message, and info message.
 * It also provides a context for the input field to use.
 * It can be recursively used to wrap nested inputs. so that shared errored or disabled states can be applied to all children inputs.
 */
export const InputWrapper = ({
	id,
	children,
	label,
	labelPosition = "top",
	justify,
	align,
	dangerouslySetClassName,
	group = false,
	required = false,
	disableLabelClick = false,
	labelRight,
	labelSize,
	labelWeight,
	...props
}: PropsWithChildren<InputWrapperProps>) => {
	const { error, disabled = props.disabled, info = props.info, readOnly = props.readOnly } = useInputField();

	const labelElement = label && (
		<InputLabel size={labelSize} weight={labelWeight}>
			{label}
		</InputLabel>
	);

	return (
		<InputFieldProvider
			id={id}
			error={props.error || error}
			disabled={disabled}
			readOnly={readOnly}
			required={required}
			info={info}
			disableLabelClick={disableLabelClick}
		>
			<div
				className={cn("flex w-full flex-col gap-2", dangerouslySetClassName)}
				role={group ? "group" : undefined}
				aria-labelledby={group ? `label-${id}` : undefined}
				data-testid={props["data-testid"]}
			>
				<div className={inputWrapperVariants({ labelPosition, justify, align })}>
					{labelPosition === "right" && children}
					{labelElement && labelRight ? (
						<div className="flex w-full items-start justify-between">
							{labelElement}
							{labelRight}
						</div>
					) : (
						labelElement
					)}
					{labelPosition !== "right" && children}
				</div>
				<AnimatePresence>
					{props?.error && (
						<SubtleTopSlideIn>
							<InputError />
						</SubtleTopSlideIn>
					)}
				</AnimatePresence>
			</div>
		</InputFieldProvider>
	);
};
